import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, FormikProps } from 'formik';
import { useAppSelector, usePermissions } from '@app/hooks';
import { selectCurrentProject } from '@features/project/projectSlice';
import { useCreateBIntactActivityMutation } from '@app/features/activities/activityApiSlice';
import GenericInputGroup from '@components/input/inputGroup/GenericInputGroup';
import ModuleButton from '@components/button/ModuleButton';
import TranslatableText from '@components/translations/TranslatableText';
import { BiSave } from 'react-icons/bi';
import { BsExclamationTriangle } from 'react-icons/bs';
import {
	activityTitleField,
	startYearField,
	endYearField,
	biodiversityActivityValidationSchema,
	type BiodiversityActivityFormValues,
} from './biodiversityActivityForm';

const NewBiodiversityActivityPage = () => {
	const navigate = useNavigate();
	const { project } = useAppSelector(selectCurrentProject);
	const { canMutateProject } = usePermissions();
	const [createActivity, { isLoading, isError }] = useCreateBIntactActivityMutation();
	const formikRef = useRef<FormikProps<BiodiversityActivityFormValues>>(null);

	const initialValues: BiodiversityActivityFormValues = useMemo(() => ({
		activityTitle: '',
		startYear: project?.start_year_of_activities ?? new Date().getFullYear(),
		endYear: project?.last_year_of_accounting ?? new Date().getFullYear() + 5,
	}), [project]);

	const handleSubmit = useCallback(async (values: BiodiversityActivityFormValues) => {
		if (!project?.id || !canMutateProject) return;
		await createActivity({
			project: project.id,
			name: values.activityTitle,
			start_year_t2: values.startYear,
			last_year_of_accounting_t2: values.endYear,
			module_types: [],
			is_b_intact: true,
		}).unwrap();
		navigate('../activities', { relative: 'path' });
	}, [project, canMutateProject, createActivity, navigate]);

	const handleCancel = useCallback(() => {
		navigate('../activities', { relative: 'path' });
	}, [navigate]);

	return (
		<section>
			<div className="module-group">
				<div className="pos-relative pb-1">
					<h2 className="fs-14 ff-medium-ext module-header w-fit">
						<TranslatableText translationKey="activity.create_new_b_intact_activity" />
					</h2>
				</div>

			<div
				className="d-flex align-items-start gap-8"
				style={{
					padding: '10px 14px',
					backgroundColor: '#fff9e6',
					border: '1px solid #ffe8b3',
					borderRadius: '6px',
					color: '#b8860b',
					maxWidth: '550px',
				}}
			>
				<BsExclamationTriangle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
				<span className="fs-12">
					<TranslatableText translationKey="activity.biodiversity_no_copy_delete_warning" />
				</span>
			</div>

			<div className="d-flex f-column" style={{ maxWidth: '550px' }}>
				<Formik
						innerRef={formikRef}
						initialValues={initialValues}
						validationSchema={biodiversityActivityValidationSchema}
						onSubmit={handleSubmit}
						enableReinitialize
					>
						{() => (
							<Form>
								<div className="d-flex f-column gap-3 pb-1">
									<div className="py-1">
										<GenericInputGroup
											inputGroup={{ ...activityTitleField, disabled: isLoading }}
											showCommentIcon={false}
										/>
									</div>
									<div className="py-1">
										<GenericInputGroup
											inputGroup={{ ...startYearField, disabled: isLoading }}
											showCommentIcon={false}
										/>
									</div>
									<div className="py-1">
										<GenericInputGroup
											inputGroup={{ ...endYearField, disabled: isLoading }}
											showCommentIcon={false}
										/>
									</div>
								</div>

								{isError && (
									<p className="text-error fs-13 pb-2">
										<TranslatableText translationKey="activity.create_activity_error" />
									</p>
								)}

								<div className="d-flex align-items-center gap-8">
									<ModuleButton
										disabled={isLoading}
										isLoading={isLoading}
										icon={BiSave}
										labelKey="main.create"
									/>
									<button
										type="button"
										className="btn fs-12"
										onClick={handleCancel}
										disabled={isLoading}
									>
										<TranslatableText translationKey="main.cancel" />
									</button>
								</div>
							</Form>
						)}
					</Formik>
				</div>
			</div>
		</section>
	);
};

export default NewBiodiversityActivityPage;
